import React from 'react';
import { useApp } from '../../context/AppContext';
import { CV_PDF_VERSION } from '../../data/cv-version';
import './Hero.css';

interface Props {
  data: {
    profileStatement: { es: string; en: string };
    heroTagline: { es: string; en: string };
    heroCTA: { es: string; en: string };
    stats: { value: string; label: { es: string; en: string } }[];
  };
}

// Recorta antes del límite sin partir palabras ni dejar un paréntesis abierto:
// prefiere cortar en la última coma o paréntesis y, si no hay, en un espacio.
const excerpt = (text: string, max: number) => {
  if (text.length <= max) return text;
  const head = text.slice(0, max);
  const clause = Math.max(head.lastIndexOf(', '), head.lastIndexOf(' ('));
  let end = clause > max / 2 ? clause : head.lastIndexOf(' ');
  // Si el corte cae dentro de un paréntesis, se corta antes de abrirlo
  const open = head.lastIndexOf(' (', end);
  if (open > head.lastIndexOf(')', end)) end = open;
  return text.slice(0, end).replace(/[\s,;:.]+$/, '') + '…';
};

const Hero: React.FC<Props> = ({ data }) => {
  const { t, mode, lang } = useApp();

  const modeLabel = {
    ejecutivo: { es: 'CV Profesional', en: 'Professional CV' },
    consejero: { es: 'Consejos de Administración', en: 'Board of Directors' },
    conferenciante: { es: 'Conferenciante', en: 'Keynote Speaker' },
  };

  return (
    <section className="hero" id="top">
      {/* Franja superior: solo la muestra el tema grafito */}
      <div className="hero__top" aria-hidden="true">
        <span>{modeLabel[mode][lang]}</span>
        <span>{t(data.heroTagline)}</span>
        <span>Madrid · Pamplona</span>
      </div>

      {/* LEFT: dark panel */}
      <div className="hero__left">
        <span className="hero__eyebrow">{modeLabel[mode][lang]}</span>

        <h1 className="hero__name">
          <span className="hero__name-line hero__name-line--1">DAVID</span>
          <span className="hero__name-line hero__name-line--2">
            LUQUIN<span className="hero__name-extra"> URTASUN</span>
          </span>
        </h1>

        <span className="hero__rule" aria-hidden="true" />

        <p className="hero__tagline">{t(data.heroTagline)}</p>

        <p className="hero__profile">{excerpt(t(data.profileStatement), 220)}</p>

        <div className="hero__actions">
          <a href="#sobre-mi" className="hero__btn hero__btn--primary">
            {lang === 'es' ? 'Ver trayectoria' : 'View career'}
          </a>
          <a
            href={`/cv/david-luquin-cv-${mode}-${lang}.pdf?v=${CV_PDF_VERSION}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hero__btn hero__btn--ghost"
          >
            {t(data.heroCTA)}
          </a>
          <a
            href="https://www.linkedin.com/in/davidluquin"
            target="_blank"
            rel="noopener noreferrer"
            className="hero__social"
            aria-label="LinkedIn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
            </svg>
          </a>
        </div>

        {/* Stats */}
        <div className="hero__stats">
          {data.stats.map(s => (
            <div key={s.value} className="hero__stat">
              <span className="hero__stat-value">{s.value}</span>
              <span className="hero__stat-label">{t(s.label)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT: full-height photo */}
      <div className="hero__right">
        <img
          src={`${process.env.PUBLIC_URL}/david-luquin.jpeg`}
          alt="David Luquin Urtasun"
          className="hero__photo"
        />
        {/* Marcas de corte y pie de foto: solo las muestra el tema grafito */}
        <span className="hero__crop hero__crop--tl" aria-hidden="true" />
        <span className="hero__crop hero__crop--tr" aria-hidden="true" />
        <span className="hero__crop hero__crop--bl" aria-hidden="true" />
        <span className="hero__crop hero__crop--br" aria-hidden="true" />
        <span className="hero__fig" aria-hidden="true">Fig. 01</span>
        <span className="hero__caption" aria-hidden="true">
          <span>David Luquin Urtasun</span>
          <span>2026</span>
        </span>
      </div>
    </section>
  );
};

export default Hero;
